import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { Progress } from "./ui/progress";
import { TrendingUp, TrendingDown, Minus, Sparkles, AlertTriangle, Lightbulb, RefreshCw, ChevronDown, ChevronUp, Brain } from "lucide-react";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const getTrendIcon = (trend) => {
  if (trend === "increasing") return TrendingUp;
  if (trend === "decreasing") return TrendingDown;
  return Minus;
};

const getTrendColor = (trend) => {
  if (trend === "increasing") return "text-red-500";
  if (trend === "decreasing") return "text-emerald-500";
  return "text-muted-foreground";
};

const getConfidenceStyle = (confidence) => {
  const styles = {
    high: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    medium: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    low: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400"
  };
  return styles[confidence] || styles.medium;
};

const CategoryItem = ({ cat, maxAmount }) => {
  const TrendIcon = getTrendIcon(cat.trend);
  const trendColor = getTrendColor(cat.trend);
  const progress = (cat.predicted_amount / maxAmount) * 100;
  
  return (
    <div className="group">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <TrendIcon className={`w-4 h-4 ${trendColor}`} />
          <span className="text-sm font-medium">{cat.category}</span>
          <Badge className={`text-xs ${getConfidenceStyle(cat.confidence)}`}>{cat.confidence}</Badge>
        </div>
        <span className="text-sm font-semibold tabular-nums">{formatCurrency(cat.predicted_amount)}</span>
      </div>
      <Progress value={progress} className="h-1.5" />
      <div className="flex justify-between mt-0.5">
        <span className="text-xs text-muted-foreground">Last: {formatCurrency(cat.last_month_amount)}</span>
        <span className="text-xs text-muted-foreground">Avg: {formatCurrency(cat.avg_3_month)}</span>
      </div>
    </div>
  );
};

export const SpendingPredictions = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [predictions, setPredictions] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const fetchPredictions = useCallback(async () => {
    try {
      setError(null);
      const response = await api.get("/insights/predictions");
      setPredictions(response.data);
    } catch (err) {
      console.error("Failed to fetch predictions:", err);
      setError("Unable to generate predictions");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api]);

  useEffect(() => {
    fetchPredictions();
  }, [fetchPredictions]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPredictions();
  };

  if (loading) {
    return (
      <Card className="border-border/50">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64 mt-1" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error || !predictions) {
    return (
      <Card className="border-border/50">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-primary" />
            <CardTitle className="font-heading">Spending Predictions</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">
            {error || "Add more transactions to enable AI predictions."}
          </p>
        </CardContent>
      </Card>
    );
  }

  const changePercent = predictions.total_last_month > 0 
    ? ((predictions.total_predicted - predictions.total_last_month) / predictions.total_last_month * 100)
    : 0;
  const isIncrease = changePercent > 0;
  const categories = predictions.predictions_by_category || [];
  const displayedCategories = expanded ? categories : categories.slice(0, 5);
  const maxAmount = Math.max(...categories.map(c => c.predicted_amount), 1);

  return (
    <Card className="border-border/50 overflow-hidden">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/20 to-purple-500/20 flex items-center justify-center">
              <Brain className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            </div>
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                Spending Predictions
                <Badge variant="secondary" className="text-xs font-normal">AI</Badge>
              </CardTitle>
              <CardDescription>Forecast for {predictions.month}</CardDescription>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={handleRefresh} disabled={refreshing} className="h-8 w-8">
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="p-4 rounded-xl bg-muted/50">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Predicted</p>
            <p className="text-2xl font-bold font-heading mt-1">{formatCurrency(predictions.total_predicted)}</p>
            <div className="flex items-center gap-1 mt-1">
              {isIncrease ? <TrendingUp className="w-3 h-3 text-red-500" /> : <TrendingDown className="w-3 h-3 text-emerald-500" />}
              <span className={`text-xs ${isIncrease ? "text-red-500" : "text-emerald-500"}`}>
                {Math.abs(changePercent).toFixed(1)}% vs last month
              </span>
            </div>
          </div>
          <div className="p-4 rounded-xl bg-muted/50">
            <p className="text-xs text-muted-foreground uppercase tracking-wide">Last Month</p>
            <p className="text-2xl font-bold font-heading mt-1">{formatCurrency(predictions.total_last_month)}</p>
            <p className="text-xs text-muted-foreground mt-1">Actual spending</p>
          </div>
        </div>

        {predictions.ai_analysis && (
          <div className="p-4 rounded-xl bg-gradient-to-r from-violet-500/5 to-purple-500/5 border border-violet-200/20">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm leading-relaxed">{predictions.ai_analysis}</p>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold">By Category</h4>
            <span className="text-xs text-muted-foreground">{categories.length} categories</span>
          </div>
          <div className="space-y-3">
            {displayedCategories.map((cat, idx) => (
              <CategoryItem key={idx} cat={cat} maxAmount={maxAmount} />
            ))}
          </div>
          {categories.length > 5 && (
            <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} className="w-full text-xs">
              {expanded ? <><ChevronUp className="w-3 h-3 mr-1" />Less</> : <><ChevronDown className="w-3 h-3 mr-1" />{categories.length - 5} More</>}
            </Button>
          )}
        </div>

        {predictions.savings_opportunities && predictions.savings_opportunities.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-amber-500" />
              Savings Opportunities
            </h4>
            <ul className="space-y-1.5">
              {predictions.savings_opportunities.slice(0, 3).map((opp, idx) => (
                <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                  <span className="text-emerald-500 mt-1">•</span>
                  {opp}
                </li>
              ))}
            </ul>
          </div>
        )}

        {predictions.warnings && predictions.warnings.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Watch Out
            </h4>
            <ul className="space-y-1.5">
              {predictions.warnings.slice(0, 2).map((warning, idx) => (
                <li key={idx} className="text-sm text-amber-600 dark:text-amber-400 flex items-start gap-2">
                  <span className="mt-1">•</span>
                  {warning}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SpendingPredictions;
